import React, { useState, useEffect, useRef } from 'react';
import { LeadStats, LeadFilters } from '../../types/Lead';
import { DashboardViewMode } from '../../types/Star';
import { CompanyStats } from '../../types/Company';
import { DashboardNavigation } from './DashboardNavigation';
import { SearchBar } from '../ui/SearchBar';
import { MultiSelectFilter } from '../ui/MultiSelectFilter';
import ScoringSettingsModal from '../scoring/ScoringSettingsModal';
import { useScoring } from '../../contexts/ScoringContext';
import TopMenuShell from './TopMenuShell';

interface TopMenuProps {
  stats: LeadStats;
  companyStats: CompanyStats;
  /** Current persisted filter selections, used to initialize the controls. */
  filters: LeadFilters;
  onFiltersChange: (filters: LeadFilters) => void;
  onSearchChange: (query: string) => void;
  searchQuery?: string;
  currentView: DashboardViewMode;
  onViewChange: (view: DashboardViewMode) => void;
  viewCounts: {
    all: number;
    starred: number;
    member: number;
    leadsWithMembers: number;
    generate: number;
    findcompanies: number;
  };
}

const EMPTY_VALUES: string[] = [];

const TopMenu: React.FC<TopMenuProps> = ({
  stats,
  companyStats,
  filters,
  onFiltersChange,
  onSearchChange,
  searchQuery = "",
  currentView,
  onViewChange,
  viewCounts,
}) => {
  const isCompanyView = currentView === 'findcompanies';
  const [selectedCompanySize, setSelectedCompanySize] = useState<string[]>(filters.companySize ?? EMPTY_VALUES);
  const [selectedSector, setSelectedSector] = useState<string[]>(filters.sector ?? EMPTY_VALUES);
  const [selectedGeography, setSelectedGeography] = useState<string[]>(filters.geography ?? EMPTY_VALUES);
  const [selectedIndustry, setSelectedIndustry] = useState<string[]>(filters.industry ?? EMPTY_VALUES);
  const [showScoringSettings, setShowScoringSettings] = useState<boolean>(false);
  const { isCustomScoring } = useScoring();

  const emit = (next: {
    companySize?: string[];
    sector?: string[];
    geography?: string[];
    industry?: string[];
  }) => {
    const toFilter = (values: string[]) => (values.length > 0 ? values : undefined);
    onFiltersChange({
      companySize: toFilter(next.companySize ?? selectedCompanySize),
      sector: toFilter(next.sector ?? selectedSector),
      geography: toFilter(next.geography ?? selectedGeography),
      industry: toFilter(next.industry ?? selectedIndustry),
    });
  };

  // Clear filters when switching between company view and lead views.
  // Skip the initial mount so persisted selections are preserved on load.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setSelectedCompanySize(EMPTY_VALUES);
    setSelectedSector(EMPTY_VALUES);
    setSelectedGeography(EMPTY_VALUES);
    setSelectedIndustry(EMPTY_VALUES);
    onFiltersChange({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompanyView]);

  const handleCompanySizeChange = (values: string[]) => {
    setSelectedCompanySize(values);
    emit({ companySize: values });
  };

  const handleSectorChange = (values: string[]) => {
    setSelectedSector(values);
    emit({ sector: values });
  };

  const handleGeographyChange = (values: string[]) => {
    setSelectedGeography(values);
    emit({ geography: values });
  };

  const handleIndustryChange = (values: string[]) => {
    setSelectedIndustry(values);
    emit({ industry: values });
  };

  const hasActiveFilters =
    selectedCompanySize.length > 0 ||
    selectedSector.length > 0 ||
    selectedGeography.length > 0 ||
    selectedIndustry.length > 0;

  const clearFilters = () => {
    setSelectedCompanySize(EMPTY_VALUES);
    setSelectedSector(EMPTY_VALUES);
    setSelectedGeography(EMPTY_VALUES);
    setSelectedIndustry(EMPTY_VALUES);
    onFiltersChange({});
  };


  return (
    <TopMenuShell>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '12px' }}>
          <div style={{ flex: 1 }}>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(4px)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                  {stats.totalLeads}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                  Total Leads
                </div>
              </div>

              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(4px)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#bbf7d0' }}>
                  {stats.topLeads}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                  Top Leads (&gt;7)
                </div>
              </div>

              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(4px)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f9d0ff' }}>
                  {viewCounts.leadsWithMembers}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                  Leads with Members
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {!isCompanyView && (
              <MultiSelectFilter
                label="Company Size"
                placeholder="All Sizes"
                options={stats.companySizes}
                selectedValues={selectedCompanySize}
                onChange={handleCompanySizeChange}
              />
            )}

            <MultiSelectFilter
              label="Sector"
              placeholder="All Sectors"
              options={isCompanyView ? companyStats.sectors : stats.sectors}
              selectedValues={selectedSector}
              onChange={handleSectorChange}
            />

            <MultiSelectFilter
              label="Geography"
              placeholder="All Locations"
              options={isCompanyView ? companyStats.geographies : stats.geographies}
              selectedValues={selectedGeography}
              onChange={handleGeographyChange}
            />

            {isCompanyView && (
              <MultiSelectFilter
                label="Industry"
                placeholder="All Industries"
                options={companyStats.industries}
                selectedValues={selectedIndustry}
                onChange={handleIndustryChange}
              />
            )}

            {hasActiveFilters && (
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Navigation positioned within the gradient area */}
        <DashboardNavigation
          currentView={currentView}
          onViewChange={onViewChange}
          counts={viewCounts}
        />

        {/* Search Bar */}
        <div style={{
          marginTop: '12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px'
        }}>
          <SearchBar
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Search companies, sectors, contacts, or locations..."
            className="dashboard-search"
          />

          {/* Scoring Settings Icon */}
          <button
            onClick={() => !isCompanyView && setShowScoringSettings(true)}
            disabled={isCompanyView}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: isCompanyView ? 'rgba(255, 255, 255, 0.4)' : 'white',
              width: '48px',
              height: '48px',
              cursor: isCompanyView ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              transition: 'all 0.2s ease',
              opacity: isCompanyView ? 0.5 : 1,
            }}
            onMouseOver={(e) => {
              if (!isCompanyView) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }}
            title={isCompanyView ? 'Not available in Find Companies view' : 'Configure Scoring Weights'}
          >
            {/* Settings Icon (Sliders/Levels) */}
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
            </svg>

            {/* Custom scoring indicator */}
            {isCustomScoring && (
              <div style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '12px',
                height: '12px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                border: '2px solid white'
              }} />
            )}
          </button>
        </div>

      {/* Scoring Settings Modal */}
      <ScoringSettingsModal
        isOpen={showScoringSettings}
        onClose={() => setShowScoringSettings(false)}
      />
    </TopMenuShell>
  );
};

export default TopMenu;